import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Package, Users, Factory, Truck, ShoppingBag, FileText,
  AlertTriangle, AlertOctagon, Container, Layers, ArrowRight, Tag,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPermission } from "../lib/permissions";
import HomeCard from "../components/HomeCard";
import FeaturedStockBoard from "../components/FeaturedStockBoard";

const fmtEUR = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const nf0 = (v) => (v ?? 0).toLocaleString("de-DE", { maximumFractionDigits: 0 });

function greeting() {
  const h = new Date().getHours();
  if (h < 11) return "Guten Morgen";
  if (h < 18) return "Guten Tag";
  return "Guten Abend";
}

const TONE = {
  danger: { box: "border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900", icon: "bg-red-500", text: "text-red-800 dark:text-red-300", link: "text-red-700 dark:text-red-300" },
  warning: { box: "border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900", icon: "bg-amber-500", text: "text-amber-800 dark:text-amber-300", link: "text-amber-700 dark:text-amber-300" },
  info: { box: "border-blue-200 bg-blue-50 dark:bg-blue-950/40 dark:border-blue-900", icon: "bg-blue-500", text: "text-blue-800 dark:text-blue-300", link: "text-blue-700 dark:text-blue-300" },
};

const AlertCard = ({ tone = "warning", icon: Icon, label, text, to, action, testid }) => {
  const t = TONE[tone] || TONE.warning;
  return (
    <Link to={to} data-testid={testid}
      className={`group flex items-center gap-3 rounded-2xl border p-4 transition-transform hover:-translate-y-0.5 ${t.box}`}>
      <div className={`h-10 w-10 shrink-0 rounded-xl text-white flex items-center justify-center ${t.icon}`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className={`overline ${t.text}`}>{label}</div>
        <div className={`text-sm font-semibold truncate ${t.text}`}>{text}</div>
      </div>
      <span className={`hidden sm:inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${t.link}`}>
        {action} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
};

function OffersSection() {
  const [offers, setOffers] = useState([]);
  const load = () => api.get("/offers").then((r) => setOffers(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (id) => { try { await api.patch(`/offers/${id}/toggle`); load(); } catch (_e) { /* noop */ } };
  return (
    <section className="mb-8" data-testid="dashboard-offers">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-bold text-lg text-[var(--text)]">Rabatt-Aktionen</h2>
        <Link to="/sonderangebote" data-testid="dashboard-offers-manage" className="text-sm font-semibold text-[var(--pop-pink)] inline-flex items-center gap-1">Verwalten <ArrowRight size={14} /></Link>
      </div>
      {offers.length === 0 ? (
        <Link to="/sonderangebote" data-testid="dashboard-offers-empty" className="block rounded-2xl border border-dashed border-[var(--border)] p-4 text-sm text-[var(--text-muted)]">Keine Aktionen angelegt – jetzt Sonderangebot erstellen →</Link>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {offers.map((o) => (
            <div key={o.id} data-testid={`dashboard-offer-${o.id}`} className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 flex items-center gap-3">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-[var(--pop-pink)] text-white flex items-center justify-center"><Tag size={18} /></div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold truncate text-[var(--text)]">{o.name}</div>
                <div className="text-xs text-[var(--text-muted)]">-{o.discount_type === "percent" ? `${o.discount_value}%` : fmtEUR(o.discount_value)}{o.ends_at ? ` · bis ${new Date(o.ends_at).toLocaleDateString("de-DE")}` : ""}</div>
                <div className="text-[11px] font-semibold mt-0.5" style={{ color: o.is_live ? "#16a34a" : "#9ca3af" }}>{o.is_live ? "Läuft" : "Inaktiv"}</div>
              </div>
              <button onClick={() => toggle(o.id)} data-testid={`dashboard-offer-toggle-${o.id}`} title={o.active ? "Deaktivieren" : "Aktivieren"}
                className={`shrink-0 relative h-6 w-11 rounded-full transition-colors ${o.active ? "bg-[var(--pop-pink)]" : "bg-neutral-300"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${o.active ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [palletSummary, setPalletSummary] = useState(null);
  const [dsvStats, setDsvStats] = useState(null);
  const [restock, setRestock] = useState([]);

  useEffect(() => {
    (async () => {
      try { setData((await api.get("/dashboard/summary")).data); } catch (_e) {}
      try { setPalletSummary((await api.get("/pallets/summary")).data); } catch (_e) {}
      try { setDsvStats((await api.get("/dsv/stats")).data); } catch (_e) {}
      try { setRestock((await api.get("/production/restock-suggestions")).data || []); } catch (_e) {}
    })();
  }, []);

  const c = data?.counts || {};
  const firstName = user?.name?.split(" ")[0] || "Team";
  const today = new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const expiredCount = data?.expired_batches?.length || 0;
  const mhdCount = data?.mhd_warnings?.length || 0;
  const lowStock = data?.low_stock?.length || 0;
  const overdueCount = c.invoices_overdue || 0;
  const palletOver = palletSummary?.over_threshold?.length || 0;
  const dsvFail = dsvStats?.health?.consecutive_failures >= 3;

  // Primary daily module cards — each gated by real read permission.
  const cards = [
    { m: "verkauf", to: "/bestellungen", title: "Bestellungen", description: "Kundenaufträge prüfen, freigeben & abwickeln", count: c.orders_open, countLabel: "offen", icon: ShoppingBag, testid: "home-card-bestellungen", secondaryLabel: "Neue Bestellung", secondaryTo: "/bestellungen?new=1" },
    { m: "produktion", to: "/produktion", title: "Produktion", description: "Produktionsaufträge & Chargen steuern", count: c.production_open, countLabel: "in Arbeit", icon: Factory, testid: "home-card-produktion" },
    { m: "verkauf", to: "/lieferscheine", title: "Lieferscheine", description: "Versandbereite Lieferungen & Versand", count: c.delivery_open, countLabel: "offen", icon: Truck, testid: "home-card-lieferscheine" },
    { m: "lager", to: "/lager", title: "Lager & Bestand", description: "Rohstoffe, Fertigware, Stellplätze & MHD", count: c.items, countLabel: "Artikel", icon: Package, testid: "home-card-lager" },
    { m: "crm", to: "/kunden", title: "Kunden", description: "Kundenstamm, Konditionen & Adressen", count: c.customers, countLabel: "aktiv", icon: Users, testid: "home-card-kunden" },
    { m: "rechnungen", to: "/rechnungen", title: "Rechnungen", description: "Fakturierung, Zahlungen & Mahnwesen", count: c.invoices_open, countLabel: "offen", icon: FileText, testid: "home-card-rechnungen" },
  ].filter((card) => hasPermission(user, card.m, "read"));

  const alerts = [];
  if (hasPermission(user, "lager", "read") && (expiredCount > 0 || mhdCount > 0)) {
    alerts.push({
      testid: "alert-mhd", tone: expiredCount > 0 ? "danger" : "warning", icon: Layers, to: "/chargen",
      label: "MHD & Chargen",
      text: expiredCount > 0
        ? `${expiredCount} abgelaufen · ${mhdCount} bald fällig`
        : `${mhdCount} Chargen laufen bald ab`,
      action: "Chargen prüfen",
    });
  }
  if (hasPermission(user, "rechnungen", "read") && overdueCount > 0) {
    alerts.push({
      testid: "alert-invoices", tone: "danger", icon: AlertOctagon, to: "/rechnungen",
      label: "Überfällige Rechnungen",
      text: `${overdueCount} überfällig · ${fmtEUR(data?.overdue_amount)}`,
      action: "Öffnen",
    });
  }
  if (hasPermission(user, "einkauf", "read") && lowStock > 0) {
    alerts.push({
      testid: "alert-stock", tone: "warning", icon: AlertTriangle, to: "/bestellvorschlaege",
      label: "Mindestbestand",
      text: `${lowStock} Artikel unter Mindestbestand`,
      action: "Nachbestellen",
    });
  }
  if (hasPermission(user, "versand", "read") && palletOver > 0) {
    alerts.push({
      testid: "alert-pallets", tone: "info", icon: Container, to: "/paletten",
      label: "Palettenkonto",
      text: `${palletOver} Kunde${palletOver === 1 ? "" : "n"} über Schwelle`,
      action: "Ansehen",
    });
  }
  if (dsvFail) {
    alerts.push({
      testid: "alert-dsv", tone: "danger", icon: AlertOctagon, to: "/einstellungen",
      label: "DSV-Tracking",
      text: `${dsvStats.health.consecutive_failures} Fehler in Folge`,
      action: "Prüfen",
    });
  }

  return (
    <div data-testid="page-dashboard">
      {/* Greeting header */}
      <header className="mb-8" data-testid="dashboard-header">
        <div className="overline mb-1">{today}</div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight text-[var(--text)]">
          {greeting()}, {firstName} 👋
        </h1>
        <p className="text-sm md:text-base text-[var(--text-muted)] mt-1">
          Wähle einen Bereich aus, um loszulegen.
        </p>
      </header>

      {/* Attention & action items */}
      {alerts.length > 0 && (
        <section className="mb-8" data-testid="attention-section">
          <h2 className="font-display font-bold text-lg text-[var(--text)] mb-3">Aufmerksamkeit & Handlungsbedarf</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 pop-fade">
            {alerts.map((a) => <AlertCard key={a.testid} {...a} />)}
          </div>
        </section>
      )}

      {/* Fertigwaren unter Mindestbestand → vorbefüllter Produktionsauftrag */}
      {hasPermission(user, "produktion", "read") && restock.length > 0 && (
        <section className="mb-8" data-testid="restock-widget">
          <h2 className="font-display font-bold text-lg text-[var(--text)] mb-3">
            Fertigwaren unter Mindestbestand ({restock.length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 pop-fade">
            {restock.map((s) => {
              const critical = s.stock <= 0;
              return (
                <Link key={s.item_id} to={`/produktion?restock=${s.item_id}`}
                  data-testid={`restock-widget-${s.item_id}`}
                  className={`group flex items-center gap-3 rounded-2xl border p-4 transition-transform hover:-translate-y-0.5 ${critical ? TONE.danger.box : TONE.warning.box}`}>
                  <div className={`h-10 w-10 shrink-0 rounded-xl text-white flex items-center justify-center ${critical ? TONE.danger.icon : TONE.warning.icon}`}>
                    <Factory size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`overline ${critical ? TONE.danger.text : TONE.warning.text}`}>
                      {s.sku || "Fertigware"}
                    </div>
                    <div className={`text-sm font-semibold truncate ${critical ? TONE.danger.text : TONE.warning.text}`}>
                      {s.name}
                    </div>
                    <div className={`text-xs mt-0.5 ${critical ? TONE.danger.link : TONE.warning.link}`}>
                      Bestand {nf0(s.stock)} / Min {nf0(s.min_stock)} · Vorschlag {nf0(s.suggested_quantity)} {s.unit || ""}
                    </div>
                    {Number(s.planned) > 0 && (
                      <div className={`text-xs mt-0.5 font-medium ${s.covered ? "text-emerald-700 dark:text-emerald-400" : (critical ? TONE.danger.link : TONE.warning.link)}`}
                           data-testid={`restock-widget-planned-${s.item_id}`}>
                        bereits {nf0(s.planned)} {s.unit || ""} in Produktion geplant{s.covered ? " · gedeckt" : ""}
                      </div>
                    )}
                  </div>
                  <span className={`hidden sm:inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${critical ? TONE.danger.link : TONE.warning.link}`}>
                    Auftrag <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {hasPermission(user, "crm", "read") && <OffersSection />}

      {/* Primary module cards */}
      <section data-testid="home-cards-section">
        <h2 className="font-display font-bold text-lg text-[var(--text)] mb-3">Deine Bereiche</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 pop-fade">
          {cards.map((card) => (
            <HomeCard
              key={card.testid}
              to={card.to}
              title={card.title}
              description={card.description}
              count={card.count ?? "—"}
              countLabel={card.countLabel}
              icon={card.icon}
              testid={card.testid}
              secondaryLabel={card.secondaryLabel}
              secondaryTo={card.secondaryTo}
            />
          ))}
        </div>
      </section>

      {/* Featured stock overview (calm, visual) */}
      {hasPermission(user, "lager", "read") && (
        <section className="mt-10">
          <FeaturedStockBoard />
        </section>
      )}
    </div>
  );
}
